/**
 * Bascule de thème. Trois états : `light`, `dark`, et `system` (aucun attribut posé,
 * `prefers-color-scheme` décide). Le choix explicite doit gagner dans les deux sens,
 * d'où l'attribut `data-theme` plutôt qu'une classe.
 *
 * L'application initiale se fait dans un script inline de `index.html`, avant peinture ;
 * ce hook ne fait que lire l'état déjà posé et le modifier.
 *
 * Depuis le chantier préférences (2026-09-15), plusieurs instances de `useTheme()` coexistent
 * dans l'arbre (barre du haut, page Préférences) et le serveur peut imposer un thème au
 * chargement de la session. Un `useState` purement local par instance les ferait diverger :
 * l'une écrirait `data-theme` sans que les autres ne le sachent. `subscribers` est le même
 * motif que `onUnauthenticated` de `lib/api.js` — un petit pub-sub interne, pas de dépendance.
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'exiplan-theme';

const read = () => {
  if (typeof document === 'undefined') return 'system';
  return document.documentElement.getAttribute('data-theme') || 'system';
};

/** Instances de `useTheme()` actuellement montées, notifiées à chaque changement. */
const subscribers = new Set();

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  try {
    // `system` RETIRE la clé plutôt que d'y écrire la chaîne 'system' : c'est l'absence de
    // clé que lit le script anti-flash d'index.html avant peinture.
    if (theme === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* navigation privée : le thème reste valable pour la session en cours */
  }
  subscribers.forEach((notify) => notify(theme));
}

export function useTheme() {
  const [theme, setThemeState] = useState(read);

  useEffect(() => {
    subscribers.add(setThemeState);
    return () => subscribers.delete(setThemeState);
  }, []);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    applyTheme(next);
  }, []);

  /*
   * Pas de `cycle()` ici. Il a existé, sans aucun appelant, et il plaçait `applyTheme()` —
   * qui écrit le DOM, `localStorage`, et déclenche un `setState` sur les autres instances
   * abonnées — DANS l'updater de `setThemeState`. Or les updaters sont invoqués pendant la
   * phase de rendu : c'est un rendu impur, que StrictMode double-invoque et que React signale
   * par « Cannot update a component while rendering a different component ». Un raccourci
   * clavier branché dessus aurait suffi à le faire éclater. Si le besoin revient, il s'écrit
   * hors de l'updater : `setTheme(suivantDe(theme))`.
   */
  return { theme, setTheme };
}
